import React, { useCallback, useState } from 'react';
import { Editor } from '@tiptap/react';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  StrikethroughIcon,
  ListIcon,
  ListOrderedIcon,
  QuoteIcon,
  LinkIcon,
  Undo2Icon,
  Redo2Icon,
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  Heading2Icon,
  Heading3Icon,
  TableIcon,
  PlusIcon,
  MinusIcon,
  Trash2Icon,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { editorStyles } from './styles';

interface RichTextToolbarProps {
  editor: Editor | null;
  disabled?: boolean;
  className?: string;
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  isActive = false,
  disabled = false,
  title,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      editorStyles.toolbarButton,
      isActive && editorStyles.toolbarButtonActive
    )}
  >
    {children}
  </button>
);

const ToolbarDivider: React.FC = () => (
  <div className="w-px h-6 bg-secondary-200 dark:bg-secondary-700 mx-1" />
);

export const RichTextToolbar: React.FC<RichTextToolbarProps> = ({
  editor,
  disabled = false,
  className,
}) => {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const iconSize = 18;

  const handleSetLink = useCallback(() => {
    if (!editor) {return;}

    if (linkUrl === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const handleLinkButtonClick = useCallback(() => {
    if (!editor) {return;}

    const previousUrl = editor.getAttributes('link').href;
    setLinkUrl(previousUrl ?? '');
    setShowLinkInput(true);
  }, [editor]);

  const handleFontSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (!editor) {return;}
      const value = e.target.value;
      if (value === 'default') {
        editor.chain().focus().unsetFontSize().run();
      } else {
        editor.chain().focus().setFontSize(value).run();
      }
    },
    [editor]
  );

  if (!editor) {return null;}

  return (
    <div className={cn(editorStyles.toolbar, className)}>
      {/* Font Size */}
      <select
        value={editor.getAttributes('textStyle').fontSize ?? 'default'}
        onChange={handleFontSizeChange}
        disabled={disabled}
        className={editorStyles.toolbarSelect}
        title="Font Size"
      >
        <option value="default">Normal</option>
        <option value="0.875rem">Small</option>
        <option value="1.25rem">Large</option>
      </select>

      <ToolbarDivider />

      {/* Headings */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive('heading', { level: 2 })}
        disabled={disabled}
        title="Heading 2"
      >
        <Heading2Icon size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive('heading', { level: 3 })}
        disabled={disabled}
        title="Heading 3"
      >
        <Heading3Icon size={iconSize} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Text Formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        disabled={disabled}
        title="Bold (Ctrl+B)"
      >
        <BoldIcon size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        disabled={disabled}
        title="Italic (Ctrl+I)"
      >
        <ItalicIcon size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        disabled={disabled}
        title="Underline (Ctrl+U)"
      >
        <UnderlineIcon size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        disabled={disabled}
        title="Strikethrough"
      >
        <StrikethroughIcon size={iconSize} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Text Alignment */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        isActive={editor.isActive({ textAlign: 'left' })}
        disabled={disabled}
        title="Align Left"
      >
        <AlignLeftIcon size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        isActive={editor.isActive({ textAlign: 'center' })}
        disabled={disabled}
        title="Align Center"
      >
        <AlignCenterIcon size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        isActive={editor.isActive({ textAlign: 'right' })}
        disabled={disabled}
        title="Align Right"
      >
        <AlignRightIcon size={iconSize} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        disabled={disabled}
        title="Bullet List"
      >
        <ListIcon size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        disabled={disabled}
        title="Numbered List"
      >
        <ListOrderedIcon size={iconSize} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Block Elements */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        disabled={disabled}
        title="Blockquote"
      >
        <QuoteIcon size={iconSize} />
      </ToolbarButton>

      {/* Link */}
      {showLinkInput ? (
        <div className="flex items-center gap-1 ml-1">
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSetLink();
              } else if (e.key === 'Escape') {
                setShowLinkInput(false);
                setLinkUrl('');
              }
            }}
            placeholder="Enter URL..."
            className={cn(
              'px-2 py-1 text-sm rounded w-48',
              'border border-secondary-300 dark:border-secondary-600',
              'bg-white dark:bg-secondary-800',
              'text-secondary-900 dark:text-white',
              'focus:outline-none focus:ring-1 focus:ring-primary-500'
            )}
            autoFocus
          />
          <button
            type="button"
            onClick={handleSetLink}
            className="px-2 py-1 text-xs bg-primary-500 text-white rounded hover:bg-primary-600"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => {
              setShowLinkInput(false);
              setLinkUrl('');
            }}
            className="px-2 py-1 text-xs bg-secondary-300 dark:bg-secondary-600 text-secondary-700 dark:text-secondary-200 rounded hover:bg-secondary-400 dark:hover:bg-secondary-500"
          >
            Cancel
          </button>
        </div>
      ) : (
        <ToolbarButton
          onClick={handleLinkButtonClick}
          isActive={editor.isActive('link')}
          disabled={disabled}
          title="Add Link"
        >
          <LinkIcon size={iconSize} />
        </ToolbarButton>
      )}

      <ToolbarDivider />

      {/* Table */}
      <ToolbarButton
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        disabled={disabled}
        title="Insert Table"
      >
        <TableIcon size={iconSize} />
      </ToolbarButton>
      {editor.isActive('table') && (
        <>
          <ToolbarButton
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            disabled={disabled}
            title="Add Column"
          >
            <PlusIcon size={iconSize - 2} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().addRowAfter().run()}
            disabled={disabled}
            title="Add Row"
          >
            <MinusIcon size={iconSize - 2} className="rotate-90" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().deleteColumn().run()}
            disabled={disabled}
            title="Delete Column"
          >
            <MinusIcon size={iconSize - 2} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().deleteRow().run()}
            disabled={disabled}
            title="Delete Row"
          >
            <MinusIcon size={iconSize - 2} className="rotate-90" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().deleteTable().run()}
            disabled={disabled}
            title="Delete Table"
          >
            <Trash2Icon size={iconSize - 2} />
          </ToolbarButton>
        </>
      )}

      <ToolbarDivider />

      {/* Undo/Redo */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={disabled || !editor.can().undo()}
        title="Undo (Ctrl+Z)"
      >
        <Undo2Icon size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={disabled || !editor.can().redo()}
        title="Redo (Ctrl+Y)"
      >
        <Redo2Icon size={iconSize} />
      </ToolbarButton>
    </div>
  );
};

export default RichTextToolbar;
